import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Avatar from "@mui/material/Avatar";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { About, ExpiryIndicator } from "@client/shared/components";
import { decodeToken, getToken, removeToken } from "@client/utils";
import { AppService } from "@client/shared/services";
import { globalStyles, sessionExpireMsg } from "@client/shared/constants";
import style from "./style";

const defaultState = {
  open: false,
  version: {},
  role: {},
  licenseExpiresOn: ""
};

export default function Header({
  elements = [],
  position = "sticky",
  responsive = true,
  isDrawerOpen = false
}) {
  const navigate = useNavigate();
  const token = getToken();
  const userData = decodeToken(token);
  const [state, setState] = useState({
    ...defaultState,
    open: isDrawerOpen
  });
  const { firstName = "", lastName = "", profileImageUri = "" } = userData;
  const handleDrawerClose = () => {
    setState((prevState) => ({
      ...prevState,
      open: false
    }));
  };
  const handleLogout = async () => {
    const { error } = await AppService.logoutUser(getToken());
    if (!error) {
      sessionStorage.removeItem("selectedUserId");
      removeToken();
      navigate("/");
    } else {
      toast.error(sessionExpireMsg);
    }
  };
  const fetchVersion = useCallback(async () => {
    const { data, error } = await AppService.getVersion();
    if (error) {
      return;
    } else {
      setState((prevState) => ({
        ...prevState,
        version: data
      }));
    }
  }, []);
  const getUserDetails = useCallback(async () => {
    const { data, error } = await AppService.getUserDetails();
    if (error) {
      return;
    } else {
      setState((prevState) => ({
        ...prevState,
        role: data.user.roleAccess,
        licenseExpiresOn: data.user?.licenseExpiresOn
      }));
    }
  }, []);
  useEffect(() => {
    fetchVersion();
    if (token) {
      getUserDetails();
    }
  }, [fetchVersion, getUserDetails]);

  return (
    <AppBar position={position} color="transparent" elevation={0}>
      <Toolbar sx={globalStyles.flex.justify.end}>
        {elements}
        <Avatar
          src={profileImageUri}
          sx={globalStyles.cursor.pointer}
          onClick={() => {
            setState((prevState) => ({
              ...prevState,
              open: !prevState.open
            }));
          }}
        />
        <Drawer open={state.open} onClose={handleDrawerClose} anchor="right">
          <Box sx={style.drawerPaper}>
            <Box sx={style.nameWrapper}>
              <Avatar src={profileImageUri} />
              <Box sx={style.userData}>
                <Typography variant="body2">
                  {firstName + " " + lastName}
                </Typography>
                <Typography sx={style.role}>{state.role?.roleName}</Typography>
              </Box>
            </Box>
            <MenuList>
              <ExpiryIndicator licenseExpiresOn={state.licenseExpiresOn} />
              <About version={state.version} />
              <MenuItem onClick={handleLogout}>
                <ListItemText primary="Sign Out" />
                <ListItemIcon sx={style.icon}>
                  <ArrowForwardIcon fontSize="small" />
                </ListItemIcon>
              </MenuItem>
            </MenuList>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}
